import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../../../services/api';
import { opisSrodowiska, skrocCommit, pobierzAktualnaWersje } from '../../../utils/wersja';

const SystemTab = () => {
  const [systemInfo, setSystemInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  // Numer wersji, commit i srodowisko — jedno zrodlo prawdy (plik VERSION + git),
  // te same dane co w zakladce Changelog. Widoczne wylacznie w panelu admina.
  const [wersja, setWersja] = useState(null);

  const loadSystemInfo = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/info');
      setSystemInfo(data);
    } catch (err) {
      console.error('Blad podczas pobierania informacji o systemie:', err);
    } finally {
      setLoading(false);
    }
    // Osobne zapytanie — jego blad nie moze zablokowac reszty informacji
    const daneWersji = await pobierzAktualnaWersje(api);
    setWersja(daneWersji);
  }, []);

  useEffect(() => {
    loadSystemInfo();
  }, [loadSystemInfo]);

  const handleExportData = async () => {
    try {
      setLoading(true);
      const { data } = await api.post('/admin/export');
      if (data.success) {
        const exportData = data.data.export_data;
        const jsonString = JSON.stringify(exportData, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `pos_system_export_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(`Dane wyeksportowane pomyslnie! Tabele: ${data.data.tables_exported}, Rekordy: ${data.data.total_records}`);
      } else {
        toast.error(`Blad: ${data.message}`);
      }
    } catch (err) {
      toast.error(`Blad: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBackupDatabase = async () => {
    try {
      setLoading(true);
      const { data } = await api.post('/admin/backup');
      if (data.success) {
        toast.success(`Kopia zapasowa utworzona pomyslnie! Plik: ${data.data.backup_filename}, Rozmiar: ${(data.data.file_size / 1024 / 1024).toFixed(2)} MB`);
      } else {
        toast.error(`Blad: ${data.message}`);
      }
    } catch (err) {
      toast.error(`Blad: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRestartServer = async () => {
    if (!window.confirm('Czy na pewno chcesz zrestartowac serwer?\n\nAplikacja bedzie niedostepna przez kilka sekund.')) return;
    try {
      setLoading(true);
      await api.post('/admin/restart-server');
      toast('Serwer zostanie zrestartowany za chwile. Odswiez strone za kilka sekund.');
      setTimeout(() => { window.location.reload(); }, 5000);
    } catch (err) {
      toast('Serwer jest restartowany. Odswiez strone za kilka sekund.');
      setTimeout(() => { window.location.reload(); }, 5000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-system-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
      <div>
        <div style={{
          backgroundColor: 'var(--card-bg)',
          border: '1px solid var(--border)',
          borderRadius: '0.375rem',
          boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)'
        }}>
          <div style={{
            padding: '0.75rem 1rem',
            borderBottom: '1px solid var(--border)',
            backgroundColor: 'var(--bg)'
          }}>
            <h6 style={{ margin: 0, fontWeight: '600', color: 'var(--text-secondary)' }}>
              ℹ️ Informacje o systemie
            </h6>
          </div>
          <div style={{ padding: '1rem' }}>
            {systemInfo ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: '500' }}>Wersja:</span>
                  <span style={{
                    border: '1px solid var(--success)', color: 'var(--success)' ,
                    padding: '0.25rem 0.5rem',
                    borderRadius: '0.25rem',
                    fontSize: '0.75rem'
                  }}>
                    {(wersja && wersja.wersja) || systemInfo.version || 'nieznana'}
                  </span>
                </div>
                {wersja && wersja.srodowisko && wersja.srodowisko !== 'nieznane' && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: '500' }}>Środowisko:</span>
                    <span
                      title={opisSrodowiska(wersja.srodowisko).opis}
                      style={{
                        backgroundColor: opisSrodowiska(wersja.srodowisko).tlo,
                        color: opisSrodowiska(wersja.srodowisko).tekst,
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.7rem',
                        fontWeight: '700',
                        letterSpacing: '0.03em'
                      }}
                    >
                      {opisSrodowiska(wersja.srodowisko).etykieta}
                    </span>
                  </div>
                )}
                {wersja && wersja.commit && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: '500' }}>Commit:</span>
                    <span style={{
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                      backgroundColor: 'var(--bg-secondary)',
                      color: 'var(--text-secondary)',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '0.25rem',
                      fontSize: '0.72rem'
                    }}>
                      {skrocCommit(wersja.commit, 8)}
                    </span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: '500' }}>Backend:</span>
                  <span style={{ color: 'var(--text-muted)' }}>Flask/Python</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: '500' }}>Frontend:</span>
                  <span style={{ color: 'var(--text-muted)' }}>React</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: '500' }}>Status:</span>
                  <span style={{
                    border: '1px solid var(--success)', color: 'var(--success)' ,
                    padding: '0.25rem 0.5rem',
                    borderRadius: '0.25rem',
                    fontSize: '0.75rem'
                  }}>
                    🟢 Aktywny
                  </span>
                </div>
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '2rem',
                color: 'var(--text-muted)'
              }}>
                <div>Ładowanie informacji...</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div>
        <div style={{
          backgroundColor: 'var(--card-bg)',
          border: '1px solid var(--border)',
          borderRadius: '0.375rem',
          boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)'
        }}>
          <div style={{
            padding: '0.75rem 1rem',
            borderBottom: '1px solid var(--border)',
            backgroundColor: 'var(--bg)'
          }}>
            <h6 style={{ margin: 0, fontWeight: '600', color: 'var(--text-secondary)' }}>
              🔧 Akcje systemowe
            </h6>
          </div>
          <div style={{ padding: '1rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <button
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#fff' ,
                  backgroundColor: 'var(--info)',
                  border: '1px solid var(--info)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
                onClick={loadSystemInfo}
              >
                🔄 Odśwież informacje
              </button>
              <button
                onClick={handleExportData}
                disabled={loading}
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#fff' ,
                  backgroundColor: loading ? 'var(--border)' : 'var(--warning)',
                  border: `1px solid ${loading ? 'var(--border)' : 'var(--warning)'}`,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}>
                📊 {loading ? 'Eksportowanie...' : 'Eksportuj dane'}
              </button>
              <button
                onClick={handleBackupDatabase}
                disabled={loading}
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#fff' ,
                  backgroundColor: loading ? 'var(--border)' : 'var(--success)',
                  border: `1px solid ${loading ? 'var(--border)' : 'var(--success)'}`,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}>
                🗄️ {loading ? 'Tworzenie kopii...' : 'Backup bazy danych'}
              </button>
              <button
                onClick={handleRestartServer}
                disabled={loading}
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#fff' ,
                  backgroundColor: loading ? 'var(--border)' : 'var(--danger)',
                  border: `1px solid ${loading ? 'var(--border)' : 'var(--danger)'}`,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}>
                🔄 {loading ? 'Restartowanie...' : 'Restart serwera'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemTab;
